import { format } from 'date-fns'

import {
  Budget,
  MonitoredBankAccount,
  MonitoredBankAccountSubtypeEnum,
  MonitoredBankAccountTransaction,
  MonitoredBankAccountTypeEnum,
  RecurringTransaction,
  RecurringTransactionFrequencyEnum,
} from '@graphql/react-apollo'

export const DEMO_ACCOUNT_ID = 'demo-123'
export const DEMO_USER_ID = 'demo-user'

const fakeCompanies = [
  'InfiniTech',
  'Stellar Coffee',
  'Pinecrest Market',
  'BlueWave Transit',
  'NorthStar Utilities',
  'Sunrise Diner',
  'Apex Outfitters',
  'Harbor Foods',
  'Cascade Pharmacy',
  'Granite Gym',
  'Maple Grocers',
  'Vivid Stream',
  'Urban Threads',
  'Riverbend Auto',
  'Willow Cafe',
  'Skyline Cinemas',
  'Crescent Hardware',
  'Lumen Energy',
  'Orchard Books',
  'Tidewater Insurance',
]

const expenseCategories = [
  'FOOD_AND_DRINK',
  'ENTERTAINMENT',
  'GENERAL_MERCHANDISE',
  'TRANSPORTATION',
  'TRAVEL',
  'RENT_AND_UTILITIES',
  'PERSONAL_CARE',
  'MEDICAL',
  'HOME_IMPROVEMENT',
  'GENERAL_SERVICES',
  'BANK_FEES',
]

const randomInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min

const randomFloat = (min: number, max: number) =>
  Math.random() * (max - min) + min

const pick = <T,>(arr: readonly T[]): T =>
  arr[Math.floor(Math.random() * arr.length)]

const addDays = (date: Date, days: number) => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

const addMonths = (date: Date, months: number) => {
  const next = new Date(date)
  next.setMonth(next.getMonth() + months)
  return next
}

// Format in local time — toISOString would shift the day for users east of UTC.
const isoDate = (date: Date) => format(date, 'yyyy-MM-dd')

export const demoMonitoredAccount: MonitoredBankAccount = {
  __typename: 'MonitoredBankAccount',
  id: DEMO_ACCOUNT_ID,
  name: 'Ant Checking Account',
  balance: 2533.12,
  balanceLastUpdatedAt: isoDate(new Date()),
  canUnlink: false,
  cardlyticsHasOffers: false,
  cardlyticsLinked: false,
  cardlyticsLinkedAt: null,
  createdAt: isoDate(new Date()),
  deletedAt: null,
  displayAccountNumber: '23912',
  logo: null,
  requireReauth: false,
  subtype: MonitoredBankAccountSubtypeEnum.Checking,
  type: MonitoredBankAccountTypeEnum.Depository,
}

const buildTransaction = (input: {
  amount: number
  date: Date
  name: string
  primaryCategory: string
}): MonitoredBankAccountTransaction => ({
  __typename: 'MonitoredBankAccountTransaction',
  id: `demo-${crypto.randomUUID()}`,
  monitoredBankAccountId: DEMO_ACCOUNT_ID,
  amount: input.amount,
  date: isoDate(input.date),
  updatedAt: input.date.toISOString(),
  deleted: false,
  ignored: false,
  pending: false,
  needsCategorization: false,
  name: input.name,
  merchantName: input.name,
  primaryCategory: input.primaryCategory,
})

export const generateDemoTransactions =
  (): MonitoredBankAccountTransaction[] => {
    const transactions: MonitoredBankAccountTransaction[] = []
    const today = new Date()

    for (let i = 0; i < 2; i++) {
      transactions.push(
        buildTransaction({
          amount: randomFloat(5, 100),
          date: today,
          name: 'InfiniTech',
          primaryCategory: pick(expenseCategories),
        }),
      )
    }

    for (let year = 0; year < 2; year++) {
      for (let month = 0; month < 12; month++) {
        const monthAnchor = addMonths(today, -(year * 12 + month))

        for (let biWeeks = 0; biWeeks < 2; biWeeks++) {
          transactions.push(
            buildTransaction({
              amount: -1200,
              date: addDays(monthAnchor, -biWeeks * 14),
              name: pick(fakeCompanies),
              primaryCategory: 'INCOME',
            }),
          )
        }

        for (let charges = 0; charges < 15; charges++) {
          transactions.push(
            buildTransaction({
              amount: randomFloat(5, 100),
              date: addDays(monthAnchor, -randomInt(0, 23)),
              name: pick(fakeCompanies),
              primaryCategory: pick(expenseCategories),
            }),
          )
        }
      }
    }

    return transactions.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    )
  }

export const generateDemoBudgets = (): Budget[] => {
  const now = new Date()
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  return expenseCategories.map((category) => ({
    __typename: 'Budget',
    id: `demo-budget-${category}`,
    userId: DEMO_USER_ID,
    category,
    amount: randomInt(100, 1000),
    month: isoDate(firstOfMonth),
    inactiveAt: null,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
  }))
}

export const generateDemoRecurringTransactions = (): RecurringTransaction[] => {
  const now = new Date()
  return Array.from({ length: 5 }).map(() => {
    const isIncome = Math.random() > 0.5
    const lastTransactionDate = addDays(now, -randomInt(1, 5))
    const nextTransactionDate = addDays(now, randomInt(0, 5))
    return {
      __typename: 'RecurringTransaction',
      id: `demo-${crypto.randomUUID()}`,
      userId: DEMO_USER_ID,
      amount: isIncome ? randomFloat(-1200, -500) : randomFloat(20, 100),
      frequency: RecurringTransactionFrequencyEnum.Monthly,
      name: pick(fakeCompanies),
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      firstTransactionDate: isoDate(addMonths(now, -randomInt(2, 12))),
      lastTransactionDate: isoDate(lastTransactionDate),
      nextTransactionDate: isoDate(nextTransactionDate),
      transactionCount: randomInt(3, 24),
    }
  })
}
